import axios from 'axios'

// --------------------------------------------
// DÉTECTION BASE URL
// --------------------------------------------
let API_BASE_URL

const hostname = window.location.hostname;

// Domaines centraux Terrilink qui utilisent l'API centralisée
// Tous les autres domaines (tenants *.terrilink.io + domaines custom) passent par le reverse proxy local
const CENTRAL_DOMAINS = ['terrilink.io', 'www.terrilink.io', 'app.terrilink.io']
const isDev = hostname === 'localhost' || hostname === '127.0.0.1'
const isCentralDomain = CENTRAL_DOMAINS.includes(hostname)

if (isDev) {
  // Developpement local : backend separe
  API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
} else if (isCentralDomain) {
  // Site principal / admin / landing / onboarding
  API_BASE_URL = 'https://api.terrilink.io/api'
} else {
  // Tenant subdomain (xxx.terrilink.io) ou domaine custom (b.bzh, app.aveyronworldwide.com, etc.)
  // API locale via le reverse proxy Nginx du host courant
  API_BASE_URL = `${window.location.origin}/api`
}

// --------------------------------------------
// AXIOS INSTANCE
// --------------------------------------------
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // ✅ SECURITY: Envoyer cookies httpOnly avec chaque requête
})

// --------------------------------------------
// REQUEST INTERCEPTOR
// ✅ SECURITY: Auth via cookie httpOnly (withCredentials: true)
// Plus de token en header Authorization (cookie = seule source)
// --------------------------------------------
axiosInstance.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {}

    // ⭐ Gestion FORM-DATA
    if (config.data instanceof FormData) {
      // Laisser le navigateur définir Content-Type + boundary
      delete config.headers['Content-Type']
    } else {
      // Sinon c'est JSON
      config.headers['Content-Type'] = 'application/json'
    }

    return config
  },
  (error) => Promise.reject(error),
)

// --------------------------------------------
// ROUTES D'AUTH (exemptées de redirection 401)
// --------------------------------------------
const AUTH_ROUTES = [
  '/auth/login',
  '/auth/check-first-login',
  '/auth/forgot-password',
  '/auth/setup-password',
  '/tenant/auth/register',
  '/auth/refresh',
]

// --------------------------------------------
// REFRESH TOKEN — Queue unique pour éviter la tempête de /refresh
// Si plusieurs requêtes 401 arrivent en même temps,
// une seule tentative de refresh est faite, les autres attendent.
// --------------------------------------------
let isRefreshing = false
let refreshSubscribers = []

function onRefreshSuccess() {
  refreshSubscribers.forEach((cb) => cb(null))
  refreshSubscribers = []
}

function onRefreshFailure(err) {
  refreshSubscribers.forEach((cb) => cb(err))
  refreshSubscribers = []
}

function waitForRefresh() {
  return new Promise((resolve, reject) => {
    refreshSubscribers.push((err) => {
      if (err) reject(err)
      else resolve()
    })
  })
}

/**
 * Vérifie si l'URL correspond à une route d'authentification
 * Ces routes ne doivent PAS déclencher de redirection sur 401
 */
const isAuthRoute = (url) => {
  if (!url) return false
  return AUTH_ROUTES.some((route) => url.includes(route))
}

/**
 * Vérifie si l'URL correspond à une route admin (système d'auth séparé)
 * Match strict: /admin/ ou /admin? ou fin de chaîne /admin
 */
const isAdminRoute = (url) => {
  if (!url) return false
  // Match: /admin, /admin/, /admin?, /admin/xxx mais PAS /administrator
  return /\/admin(?:\/|$|\?)/.test(url)
}

/**
 * Vérifie si c'est une erreur liée au tenant (503/400)
 * Liste exhaustive des codes d'erreur tenant du backend
 * NOTE: SUBSCRIPTION_* sont gérés par 402, pas ici
 */
const isTenantError = (errorData) => {
  if (!errorData) return false

  // Codes d'erreur explicites du backend (strictement tenant-related)
  const TENANT_ERROR_CODES = [
    'TENANT_NOT_FOUND',
    'TENANT_SUSPENDED',
    'TENANT_DELETED',
    'TENANT_DB_UNAVAILABLE',
    'ORGANIZATION_NOT_FOUND',
    // NOTE: SUBSCRIPTION_* retirés - gérés par status 402
  ]

  if (TENANT_ERROR_CODES.includes(errorData.error) || TENANT_ERROR_CODES.includes(errorData.code)) {
    return true
  }

  // Messages d'erreur legacy (français)
  const TENANT_ERROR_MESSAGES = [
    'Base de données indisponible',
    'Slug invalide',
    'Configuration incomplète',
  ]

  if (TENANT_ERROR_MESSAGES.includes(errorData.error)) {
    return true
  }

  // Patterns dans le message (fallback strict)
  const message = errorData.message || ''
  return (
    message.includes('organizationSlug') ||
    message.includes('base de données') ||
    message.includes('tenant') ||
    message.includes('organisation introuvable')
  )
}

/**
 * Nettoie le localStorage en préservant les données admin (système admin séparé)
 * IMPORTANT: Toujours utiliser cette fonction au lieu de localStorage.clear() direct
 */
const clearStoragePreserveAdmin = () => {
  const adminUser = localStorage.getItem('adminUser')
  const adminUserId = localStorage.getItem('adminUserId')
  // Préserver la langue UI choisie (i18n STORAGE_KEY) à travers le logout.
  const lang = localStorage.getItem('terrilink_lang')
  localStorage.clear()
  if (adminUser) localStorage.setItem('adminUser', adminUser)
  if (adminUserId) localStorage.setItem('adminUserId', adminUserId)
  if (lang) localStorage.setItem('terrilink_lang', lang)
}

/**
 * Retourne l'URL courante (pathname + search) pour comparaison anti-boucle
 */
const getCurrentUrlPath = () => {
  return window.location.pathname + window.location.search
}

/**
 * Extrait le paramètre 'error' d'une URL ou de la query string courante
 */
const getErrorParam = (url) => {
  try {
    const searchStr = url.includes('?') ? url.split('?')[1] : ''
    const params = new URLSearchParams(searchStr)
    return params.get('error') || null
  } catch {
    return null
  }
}

/**
 * Redirige vers targetUrl seulement si cela apporte une information nouvelle
 * Évite les boucles de redirection infinies tout en permettant l'ajout d'info UX
 * @param {string} targetUrl - URL cible (ex: '/login?error=unauthorized')
 * @returns {boolean} - true si redirection effectuée, false sinon
 */
const safeRedirect = (targetUrl) => {
  const currentUrl = getCurrentUrlPath()

  // Comparaison stricte: pathname + query string identiques
  if (currentUrl === targetUrl) {
    console.log('⚠️ safeRedirect: URL identique', targetUrl, '- pas de redirection')
    return false
  }

  const targetPathname = targetUrl.split('?')[0]
  const currentPathname = window.location.pathname

  // Cas spécial /login: gestion fine des erreurs avec priorité
  if (targetPathname === '/login' && currentPathname === '/login') {
    const currentError = getErrorParam(window.location.search)
    const targetError = getErrorParam(targetUrl)

    // Si même type d'erreur (ou les deux sans erreur), bloquer
    if (currentError === targetError) {
      console.log('⚠️ safeRedirect: déjà sur /login avec error=' + currentError + ' - pas de redirection')
      return false
    }

    // Si l'URL courante a déjà un paramètre error
    if (currentError) {
      // tenant_config est prioritaire: peut remplacer une autre erreur
      // (ex: session expirée PUIS tenant DB down = afficher tenant_config)
      if (targetError === 'tenant_config' && currentError !== 'tenant_config') {
        console.log('↪️ safeRedirect: tenant_config prioritaire sur', currentError)
        // Autoriser la redirection
      } else {
        // Sinon bloquer (évite boucle unauthorized -> autre -> unauthorized)
        console.log('⚠️ safeRedirect: /login a déjà error=' + currentError + ' - pas de redirection')
        return false
      }
    }

    // Pas de currentError: on ajoute une info nouvelle
    if (!currentError) {
      console.log('↪️ safeRedirect: ajout info sur /login:', currentError, '→', targetError)
    }
  }

  console.log('↪️ safeRedirect:', currentUrl, '→', targetUrl)
  window.location.href = targetUrl
  return true
}

// --------------------------------------------
// RESPONSE INTERCEPTOR (CONSOLIDÉ P1 - DURCI)
// Source unique de vérité pour la gestion des erreurs HTTP
// --------------------------------------------
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error

    // Pas de réponse = erreur réseau
    if (!response) return Promise.reject(error)

    const status = response.status
    const requestUrl = config?.url || ''
    const errorData = response.data

    // ═══════════════════════════════════════════════════════════
    // 403 MODULE_DISABLED - Module désactivé pour ce plan
    // ═══════════════════════════════════════════════════════════
    // Stratégie: Laisser l'erreur remonter au composant avec un flag
    // Le composant peut alors afficher un message approprié ou un état vide
    // Robustesse: détecte error OU code (selon la réponse backend)
    const isModuleDisabled =
      status === 403 &&
      (errorData?.error === 'MODULE_DISABLED' || errorData?.code === 'MODULE_DISABLED')

    if (isModuleDisabled) {
      console.warn(`🔇 Module désactivé: ${errorData?.message || 'module inconnu'}`)
      const moduleError = new Error(errorData?.message || "Ce module n'est pas disponible")
      moduleError.status = 403
      moduleError.code = 'MODULE_DISABLED'
      moduleError.moduleName = errorData?.module || null
      moduleError.response = response
      return Promise.reject(moduleError)
    }

    // ═══════════════════════════════════════════════════════════
    // 403 PASSWORD_CHANGE_REQUIRED - Mot de passe provisoire non changé
    // (enforcement serveur, backstop du flag localStorage)
    // ═══════════════════════════════════════════════════════════
    if (status === 403 && errorData?.code === 'PASSWORD_CHANGE_REQUIRED') {
      // Re-synchroniser le flag (défensif : localStorage a pu être vidé alors
      // que le serveur enforce encore) puis rediriger vers l'écran obligatoire.
      // safeRedirect intègre l'anti-boucle (no-op si déjà sur /change-password).
      localStorage.setItem('tempPassword', 'true')
      safeRedirect('/change-password')
      const pcErr = new Error(errorData?.message || 'Changement de mot de passe requis')
      pcErr.status = 403
      pcErr.code = 'PASSWORD_CHANGE_REQUIRED'
      pcErr.response = response
      return Promise.reject(pcErr)
    }

    // ═══════════════════════════════════════════════════════════
    // 401 UNAUTHORIZED - Session expirée ou identifiants invalides
    // ═══════════════════════════════════════════════════════════
    if (status === 401) {
      const errorCode = errorData?.code

      // Routes d'auth: ne PAS rediriger, laisser le composant gérer (toast login)
      if (isAuthRoute(requestUrl)) {
        console.log('🔐 401 sur route auth, pas de redirection:', requestUrl)
        // Erreur remonte au composant pour afficher toast
      }
      // Routes admin: système d'auth séparé, ne pas interférer
      else if (isAdminRoute(requestUrl)) {
        console.log('🔐 401 sur route admin, pas de redirection globale')
      }
      // Access token expiré → tenter un refresh transparent (queue unique)
      else if (errorCode === 'ACCESS_TOKEN_EXPIRED' && !config._isRetryAfterRefresh) {
        if (!isRefreshing) {
          isRefreshing = true
          console.log('🔄 Access token expiré, tentative de refresh...')
          try {
            await axiosInstance.post('/auth/refresh')
            isRefreshing = false
            onRefreshSuccess()
            // Rejouer la requête originale
            config._isRetryAfterRefresh = true
            return axiosInstance(config)
          } catch (refreshError) {
            isRefreshing = false
            onRefreshFailure(refreshError)
            console.warn('🔒 Refresh échoué, redirection vers login')
            clearStoragePreserveAdmin()
            const targetUrl =
              '/login?error=unauthorized&message=' +
              encodeURIComponent('Votre session a expiré. Veuillez vous reconnecter.')
            safeRedirect(targetUrl)
          }
        } else {
          // Un refresh est déjà en cours → attendre puis rejouer
          try {
            await waitForRefresh()
            config._isRetryAfterRefresh = true
            return axiosInstance(config)
          } catch {
            // Le refresh a échoué, l'initiateur gère déjà la redirection
          }
        }
      }
      // Token invalide ou refresh échoué → redirection login
      else {
        console.warn('🔒 Session invalide, redirection vers login')
        clearStoragePreserveAdmin()
        const targetUrl =
          '/login?error=unauthorized&message=' +
          encodeURIComponent('Votre session a expiré. Veuillez vous reconnecter.')
        safeRedirect(targetUrl)
      }
    }

    // ═══════════════════════════════════════════════════════════
    // 402 PAYMENT REQUIRED - Abonnement requis/expiré
    // ═══════════════════════════════════════════════════════════
    if (status === 402) {
      const isAdmin = localStorage.getItem('userRole') === 'admin'
      const targetPath = isAdmin ? '/subscription/expired' : '/subscription/required'

      // Vérifier si on doit rediriger (pas déjà sur la page cible)
      const shouldRedirect = getCurrentUrlPath() !== targetPath

      if (shouldRedirect) {
        // Écrire sessionStorage AVANT la redirection
        // (garantit que la donnée est disponible sur la page cible)
        if (errorData) {
          sessionStorage.setItem(
            'subscriptionError',
            JSON.stringify({
              message: errorData.message,
              subscription: errorData.subscription,
            }),
          )
        }
        safeRedirect(targetPath)
      }
      // Si déjà sur la page cible: ne rien faire (pas de redirect, pas d'écriture)
    }

    // ═══════════════════════════════════════════════════════════
    // 503 - Erreur serveur/tenant (toujours rediriger)
    // ═══════════════════════════════════════════════════════════
    if (status === 503 && !isAdminRoute(requestUrl)) {
      console.error('❌ Erreur 503 détectée:', errorData)
      clearStoragePreserveAdmin()
      const errorMessage = encodeURIComponent(
        errorData?.message || 'Service temporairement indisponible.',
      )
      safeRedirect(`/login?error=tenant_config&message=${errorMessage}`)
    }

    // ═══════════════════════════════════════════════════════════
    // 400 - Erreur tenant UNIQUEMENT si isTenantError() true
    // Sinon: laisser remonter (erreur de validation normale)
    // ═══════════════════════════════════════════════════════════
    if (status === 400 && !isAdminRoute(requestUrl) && isTenantError(errorData)) {
      console.error('❌ Erreur tenant 400 détectée:', errorData)
      clearStoragePreserveAdmin()
      const errorMessage = encodeURIComponent(
        errorData?.message ||
          'Configuration de votre organisation introuvable. Veuillez contacter un administrateur.',
      )
      safeRedirect(`/login?error=tenant_config&message=${errorMessage}`)
    }

    // ═══════════════════════════════════════════════════════════
    // Normalisation de l'erreur pour tous les cas
    // ═══════════════════════════════════════════════════════════
    const serverMessage = errorData?.message || errorData?.error || error.message
    const enhancedError = new Error(serverMessage)
    enhancedError.status = status
    enhancedError.code = errorData?.code || errorData?.error || `HTTP_${status}`
    enhancedError.response = response
    enhancedError.originalError = error

    return Promise.reject(enhancedError)
  },
)

// --------------------------------------------
// SIMPLE WRAPPERS
// --------------------------------------------
export async function get(url, cfg = {}) {
  return (await axiosInstance.get(url, cfg)).data
}
export async function post(url, data = {}, cfg = {}) {
  return (await axiosInstance.post(url, data, cfg)).data
}
export async function put(url, data = {}, cfg = {}) {
  return (await axiosInstance.put(url, data, cfg)).data
}
export async function del(url, cfg = {}) {
  return (await axiosInstance.delete(url, cfg)).data
}
export async function patch(url, data = {}, cfg = {}) {
  return (await axiosInstance.patch(url, data, cfg)).data
}

// ✅ CORRECTION PHASE 5: Upload avec support PUT/POST/PATCH
/**
 * Upload file avec support PUT/POST/PATCH
 * @param {string} url - Endpoint
 * @param {FormData} formData - Form data with files
 * @param {object} options - { method: 'POST'|'PUT'|'PATCH', onProgress: function }
 */
export async function uploadFile(url, formData, options = {}) {
  const { method = 'POST', onProgress } = options

  const config = {
    headers: {}, // ne rien fixer ! axios gère automatiquement
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(percentCompleted)
        }
      : undefined,
  }

  let response
  switch (method.toUpperCase()) {
    case 'PUT':
      response = await axiosInstance.put(url, formData, config)
      break
    case 'PATCH':
      response = await axiosInstance.patch(url, formData, config)
      break
    case 'POST':
    default:
      response = await axiosInstance.post(url, formData, config)
      break
  }

  return response.data
}

// Helpers
export function getApiUrl() {
  return API_BASE_URL
}

// ✅ SECURITY: L'auth est gérée par cookie httpOnly (non accessible JS)
// Cette fonction vérifie uniquement si une session semble active côté UI
export function isAuthenticated() {
  const userId = localStorage.getItem('userId')
  // Si userId existe, l'utilisateur s'est connecté avec succès
  // La vraie validation se fait côté serveur via le cookie
  return !!userId
}

// ✅ SECURITY: Token n'est plus stocké en localStorage (cookie httpOnly)
// Cette fonction est conservée pour rétro-compatibilité mais retourne null
export function getToken() {
  return null // Token géré par cookie httpOnly uniquement
}

export { axiosInstance }

export default {
  get,
  post,
  put,
  delete: del,
  patch,
  uploadFile,
  getApiUrl,
  getToken,
  isAuthenticated,
  axios: axiosInstance,
}
